<script setup>
import { defineProps, ref, onMounted, computed, watch, defineEmits } from "vue";

const audio = ref(null);
const isPlaying = ref(false);
const progress = ref(0);
const currentTime = ref(0);
const duration = ref(0);
const volume = ref(0.5);
const isMuted = ref(false);
const isDragging = ref(false);

const audio_player = ref(null);

const emits = defineEmits(["sbu_audio_open"]);

const props = defineProps({
  media_name: String,
  media_link: String,
  audio_open: Boolean,
});

const togglePlay = () => {
  if (audio.value.paused && props.audio_open) {
    audio.value.play();
    isPlaying.value = true;
  } else {
    audio.value.pause();
    isPlaying.value = false;
  }
};

const toggleMute = () => {
  isMuted.value = !isMuted.value;
  audio.value.muted = isMuted.value;
};

const changeVolume = () => {
  audio.value.volume = volume.value;
};

const updateProgress = () => {
  if (!isDragging.value) {
    currentTime.value = audio.value.currentTime;
    progress.value = (currentTime.value / duration.value) * 100;
  }
};

const startDrag = (event) => {
  isDragging.value = true;
  dragProgress(event);
};

const dragProgress = (event) => {
  if (isDragging.value) {
    const progressBar = event.currentTarget;
    const rect = progressBar.getBoundingClientRect();
    const clickPosition = (event.clientX - rect.left) / rect.width;

    const boundedPosition = Math.max(0, Math.min(1, clickPosition));

    progress.value = boundedPosition * 100;
    currentTime.value = boundedPosition * duration.value;
  }
};

const endDrag = () => {
  if (isDragging.value) {
    audio.value.currentTime = currentTime.value;
    isDragging.value = false;
  }
};

const formatTime = (time) => {
  const minutes = Math.floor(time / 60);
  const seconds = Math.floor(time % 60);
  return `${minutes}:${seconds < 10 ? "0" : ""}${seconds}`;
};

const formattedCurrentTime = computed(() => formatTime(currentTime.value));
const formattedDuration = computed(() => formatTime(duration.value));

watch(
  () => props.media_name,
  () => {
    console.log("切換音檔");
    audio.value.pause();
    isPlaying.value = false;
    audio.value = new Audio(props.media_link.replace(/\s/g, "%20"));
    audio.value.volume = volume.value;
    audio.value.addEventListener("timeupdate", updateProgress);
    audio.value.addEventListener("loadedmetadata", () => {
      duration.value = audio.value.duration;
    });
    audio.value.addEventListener("ended", () => {
      isPlaying.value = false;
      progress.value = 0;
      currentTime.value = 0;
    });
    audio.value.currentTime = 0;

    // 自动播放
    if (props.audio_open) {
      togglePlay();
    }
  }
);

watch(
  () => props.audio_open,
  () => {
    console.log("關閉音檔");
    if (!props.audio_open) {
      audio.value.pause();
    }
  }
);

function off_audio() {
  emits("sbu_audio_open", false);
}

const adjustZoom = (view) => {
  const baseWidth = 1920;
  const baseHeight = 1080;

  const windowWidth = window.innerWidth;
  const windowHeight = window.innerHeight;

  const scaleWidth = windowWidth / baseWidth;
  const scaleHeight = windowHeight / baseHeight;

  const scale = Math.min(scaleWidth, scaleHeight);

  if (view) {
    view.style.transform = `scale(${scale})`;
  }
};

onMounted(() => {
  adjustZoom(audio_player.value);

  audio.value = new Audio(props.media_link.replace(/\s/g, "%20"));
  audio.value.volume = volume.value;
  audio.value.addEventListener("timeupdate", updateProgress);
  audio.value.addEventListener("loadedmetadata", () => {
    duration.value = audio.value.duration;
  });
  audio.value.addEventListener("ended", () => {
    isPlaying.value = false;
    progress.value = 0;
    currentTime.value = 0;
  });

  // 自动播放
  if (props.audio_open) {
    togglePlay();
  }
});
</script>

<template>
  <div class="audio-player" ref="audio_player">
    <button id="off_audio" @click="off_audio()">
      <img src="@/assets/images/audio/close.svg" alt="icon" />
    </button>

    <!-- <marquee v-if="isPlaying">
      <img src="@/assets/images/audio/music_player.svg" alt="icon" />
      <span>{{ media_name }}</span>
    </marquee> -->

    <h5>
      <img src="@/assets/images/audio/music_player.svg" alt="icon" />
      <span>{{ media_name }}</span>
    </h5>

    <div class="content">
      <button class="play_btn" @click="togglePlay">
        <img
          src="@/assets/images/audio/pause_btn.svg"
          alt="icon"
          v-if="isPlaying"
        />
        <img src="@/assets/images/audio/play_btn.svg" alt="icon" v-else />
      </button>

      <div
        class="progress-bar"
        @mousedown="startDrag"
        @mousemove="dragProgress"
        @mouseup="endDrag"
        @mouseleave="endDrag"
      >
        <div class="progress" :style="{ width: `${progress}%` }"></div>
      </div>

      <button class="mute" @click="toggleMute">
        <img
          src="@/assets/images/audio/mute_off_btn.svg"
          alt="icon"
          v-if="isMuted"
        />
        <img src="@/assets/images/audio/mute_btn.svg" alt="icon" v-else />
      </button>
      <!-- <input type="range" v-model="volume" @input="changeVolume" min="0" max="1" step="0.01"> -->

      <span class="text">
        {{ formattedCurrentTime }}／{{ formattedDuration }}
      </span>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.audio-player {
  position: fixed;
  left: 0;
  right: 0;
  bottom: 15px;
  margin: auto;
  z-index: 1;
  width: 820px;
  height: 110px;
  background-color: #fff;
  padding: 0 20px;
  box-sizing: border-box;
  border-radius: 10px;

  marquee,
  h5 {
    width: 100%;
    max-width: 535px;
    margin-left: auto;
    margin-right: auto;
    font-size: 24px;
    font-weight: 700;
    color: #2f4672;
    text-align: center;
    display: flex;
    justify-content: center;
    align-items: center;
    box-sizing: border-box;
    padding: 15px;
    span {
      margin-left: 15px;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }
  .content {
    display: flex;
    align-items: center;
    .play_btn {
      width: 34.55px;
      height: 30px;
      margin-right: 20px;
      background-color: transparent;

      svg,
      img {
        width: 100%;
        height: 100%;
      }
    }

    input[type="range"] {
      margin: 0 10px;
      width: 100px;
    }

    .progress-bar {
      position: relative;
      width: 100%;
      height: 10px;
      background: #d6d6d6;
      margin: 0 10px;
      cursor: pointer;
      width: calc(100% - 30px - 20px - 35px - 30px - 150px);
      border-radius: 10px;

      .progress {
        position: absolute;
        height: 100%;
        background: #5dbaf3;
        width: 0;
        border-radius: 10px;

        &::after {
          content: "";
          background-color: #2f4672;
          width: 10px;
          height: 30px;
          border-radius: 10px;
          position: absolute;
          right: 0;
          top: 50%;
          transform: translate(50%, -50%);
        }
      }
    }

    .mute {
      width: 35px;
      height: 30px;
      margin-left: 15px;
      margin-right: 15px;
      background-color: transparent;
    }

    .text {
      font-size: 24px;
      font-weight: 700;
      color: #2f4672;
      width: 150px;
    }
  }

  #off_audio {
    padding: 0;
    margin: 0;
    position: absolute;
    right: 5px;
    top: 5px;
    width: 25px;
    height: 25px;
    display: flex;
    justify-content: center;
    align-items: center;
    background-color: transparent;
    img {
      width: 100%;
      height: 100%;
    }
  }
}
</style>
